// Page copy that's structured data (lists the templates loop over) lives
// here so HomeView reads as layout, not a wall of strings.

// The four classifications polysigma-api returns
// (apps.detection.enums.DetectionResult), in order along the scale from a
// camera capture to a fully synthetic image.
export const VERDICTS = [
    {
        value: 'real',
        name: 'Real',
        tone: 'real',
        meaning: 'A camera capture with no evidence of editing or synthesis.',
    },
    {
        value: 'real_manipulated',
        name: 'Digitally edited',
        tone: 'edited',
        meaning: 'A camera capture changed with conventional software, such as cropping, color correction or retouching.',
    },
    {
        value: 'ai_manipulated',
        name: 'AI-edited',
        tone: 'ai-edited',
        meaning: 'A camera capture in which one or more regions were added, removed or replaced by a generative model.',
    },
    {
        value: 'ai_generated',
        name: 'AI-generated',
        tone: 'ai',
        meaning: 'An image produced entirely by a generative model, with no camera capture behind it.',
    },
]

// The independent analyses whose scores are combined, in the order the
// fusion figure stacks them (its streak lengths are set per row in
// EvidenceFusion).
export const METHODS = [
    {
        id: 'metadata',
        name: 'Metadata and provenance',
        field: 'Digital forensics',
        body: 'EXIF and container metadata record the capture device, the software that last wrote the file and the encoding settings. A consistent camera profile supports a capture. Generator signatures, editing software, and fields a camera would normally have written but which are missing all count as evidence.',
    },
    {
        id: 'watermarks',
        name: 'Watermark detection',
        field: 'Computer vision',
        body: 'Some image generators mark their output with visible or invisible watermarks. When one is found it attributes the image directly. Most generated images carry no watermark, so a negative result has no weight by itself.',
    },
    {
        id: 'pixels',
        name: 'Pixel statistics',
        field: 'Signal processing',
        body: 'Camera sensors leave a characteristic noise pattern and JPEG compression leaves quantization traces. Generated pixels lack the sensor pattern, upsampling adds periodic peaks to the frequency spectrum, and an edited region often has a different compression history from the rest of the image.',
    },
    {
        id: 'learned',
        name: 'Learned classifiers',
        field: 'Deep learning',
        body: 'Neural network classifiers trained on large labeled sets of camera and generated images pick up cues that are difficult to specify by hand. They are retrained as new generators are released, including generators designed to avoid detection.',
    },
]

// Building detection in-house vs. using PolySigma - a b-table in the page,
// stacked into labeled rows on phones.
export const COMPARISON_FIELDS = [
    { key: 'concern', label: '' },
    { key: 'inHouse', label: 'Building a detector in-house' },
    { key: 'polysigma', label: 'Using PolySigma' },
]

export const COMPARISON = [
    {
        concern: 'Training data',
        inHouse: 'Labeled camera and synthetic images from every generator of interest, collected again whenever a new one is released.',
        polysigma: 'Collected, labeled and updated by us.',
    },
    {
        concern: 'Infrastructure',
        inHouse: 'GPU capacity for training and inference, plus deployment, versioning and monitoring for each model.',
        polysigma: 'An HTTPS endpoint.',
    },
    {
        concern: 'New generators',
        inHouse: 'Accuracy declines on generators released after the training data was collected. The decline produces no errors and is usually noticed late.',
        polysigma: 'Models are retrained as generators are released. No change to your integration.',
    },
    {
        concern: 'Output stability',
        inHouse: 'Each retrain changes the distribution of scores, so thresholds built on them need re-tuning.',
        polysigma: 'The same four classes and the same 0 to 100 confidence scale across model updates.',
    },
    {
        concern: 'Interpretability',
        inHouse: 'Built and maintained separately.',
        polysigma: 'A heatmap with each AI classification.',
    },
    {
        concern: 'Staffing',
        inHouse: 'Specialists in image forensics and machine learning for as long as detection is needed.',
        polysigma: 'The engineers who already maintain your backend.',
    },
]

export const ROADMAP = [
    { name: 'Images', state: 'In private beta', current: true },
    { name: 'Video', state: 'In development', current: false },
    { name: 'Audio', state: 'In development', current: false },
]

// Values match polysigma-api's apps.beta.enums.BetaMonthlyVolume
export const VOLUME_OPTIONS = [
    { value: null, text: 'Choose a range', disabled: true },
    { value: 'under_10k', text: 'Under 10,000' },
    { value: '10k_100k', text: '10,000 to 100,000' },
    { value: '100k_1m', text: '100,000 to 1 million' },
    { value: 'over_1m', text: 'More than 1 million' },
    { value: 'unknown', text: 'Not sure yet' },
]
